/**
 * Oath User Settings Service
 * API calls for user settings management
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// Setting Type Definitions
// ============================================

/**
 * Get all active setting type definitions (metadata only, no user values)
 * Used to dynamically build the settings UI
 * @returns {Promise<{success: boolean, data?: Array<{settingKey: string, settingName: string, settingDescription: string, defaultValue: string, dataType: string, category: string, displayOrder: number}>, error?: string}>}
 */
export const getSettingTypes = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/settings/types`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch setting types' };
    }

    const result = await parseResponse(response);
    return { success: true, data: result.data || result };
  } catch (error) {
    console.error('Error fetching setting types:', error);
    return { success: false, error: 'Network error fetching setting types' };
  }
};

// ============================================
// Settings Endpoints
// ============================================

/**
 * Get all user settings grouped by category
 * @returns {Promise<{success: boolean, settings?: Object, error?: string}>}
 */
export const getAllSettings = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/settings`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch settings' };
    }

    const settings = await parseResponse(response);
    return { success: true, settings };
  } catch (error) {
    console.error('Error fetching user settings:', error);
    return { success: false, error: 'Network error fetching settings' };
  }
};

/**
 * Get a single setting by key
 * @param {string} key - The setting key (e.g., 'dark-mode')
 * @returns {Promise<{success: boolean, setting?: Object, error?: string}>}
 */
export const getSetting = async (key) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/settings/${key}`);

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'Setting not found' };
      }
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch setting' };
    }

    const setting = await parseResponse(response);
    return { success: true, setting };
  } catch (error) {
    console.error(`Error fetching setting ${key}:`, error);
    return { success: false, error: 'Network error fetching setting' };
  }
};

/**
 * Update a setting value
 * @param {string} key - The setting key (e.g., 'dark-mode')
 * @param {string|boolean|number} value - The new value (will be converted to string)
 * @returns {Promise<{success: boolean, setting?: Object, error?: string}>}
 */
export const updateSetting = async (key, value) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/settings/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ value: String(value) }),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update setting' };
    }

    const result = await parseResponse(response);
    return { success: true, setting: result.setting || result };
  } catch (error) {
    console.error(`Error updating setting ${key}:`, error);
    return { success: false, error: 'Network error updating setting' };
  }
};

/**
 * Reset all settings to defaults
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const resetAllSettings = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/settings/reset`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to reset settings' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error resetting settings:', error);
    return { success: false, error: 'Network error resetting settings' };
  }
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get the effective value of a setting (user value or default)
 * @param {Object} setting - The setting object
 * @returns {string} The effective value
 */
export const getEffectiveValue = (setting) => {
  return setting?.value ?? setting?.defaultValue ?? null;
};

/**
 * Parse a boolean setting value
 * @param {Object} setting - The setting object
 * @returns {boolean} The boolean value
 */
export const parseBooleanSetting = (setting) => {
  const value = getEffectiveValue(setting);
  return value === 'true' || value === true;
};

/**
 * Parse an integer setting value
 * @param {Object} setting - The setting object
 * @returns {number} The integer value
 */
export const parseIntegerSetting = (setting) => {
  const value = getEffectiveValue(setting);
  return parseInt(value, 10) || 0;
};

/**
 * Setting keys constants for easy reference
 */
export const SettingKeys = {
  // Appearance
  DARK_MODE: 'dark-mode',
  THEME_COLOR: 'theme-color',
  COMPACT_MODE: 'compact-mode',
  // Localization
  LANGUAGE: 'language',
  TIMEZONE: 'timezone',
  DATE_FORMAT: 'date-format',
  // Notifications
  NOTIFICATIONS_EMAIL: 'notifications-email',
  NOTIFICATIONS_SMS: 'notifications-sms',
  NOTIFICATIONS_PUSH: 'notifications-push',
  // Dashboard
  DASHBOARD_DEFAULT_VIEW: 'dashboard-default-view',
  DASHBOARD_REFRESH_INTERVAL: 'dashboard-refresh-interval',
  // Tables
  TABLE_ROWS_PER_PAGE: 'table-rows-per-page',
  TABLE_DENSE_PADDING: 'table-dense-padding',
  // Session
  SESSION_TIMEOUT_WARNING: 'session-timeout-warning',
  REMEMBER_LAST_AGENCY: 'remember-last-agency',
  LAST_SELECTED_AGENCY: 'last-selected-agency',
};

/**
 * Setting categories
 */
export const SettingCategories = {
  APPEARANCE: 'APPEARANCE',
  LOCALIZATION: 'LOCALIZATION',
  NOTIFICATIONS: 'NOTIFICATIONS',
  DASHBOARD: 'DASHBOARD',
  TABLES: 'TABLES',
  SESSION: 'SESSION',
};
